// The phone's photo upload queue: the pure part. No DOM, no IndexedDB, no network, so
// every rule the worker follows is testable here. The store (`upload-queue-store.ts`),
// the sender (`upload-sender.ts`) and the provider (`hooks/use-upload-queue.ts`) are thin
// around it.

import type { UPLOAD_QUEUE_STATUSES, UploadChannel } from "@/lib/enums";
import { MAX_UPLOAD_BYTES, uploadDuplicateSchema, uploadResponseSchema } from "@/schemas/uploads";

export type UploadStatus = (typeof UPLOAD_QUEUE_STATUSES)[number];

export interface QueuedUpload {
  /** crypto.randomUUID(), also the photo id on the server (which makes resends safe). */
  id: string;
  projectId: string;
  projectName: string;
  albumId: string;
  albumName: string;
  itemId: string | null;
  itemName: string | null;
  via: UploadChannel;
  /** The photo. Emptied once it is sent; the row stays for the "sent today" list. */
  blob: Blob;
  fileName: string;
  mime: string;
  /** "YYYY-MM-DDTHH:mm:ss", the local wall clock. */
  capturedAt: string | null;
  lat: number | null;
  lng: number | null;
  accuracy: number | null;
  device: string | null;
  /** Epoch ms, when queued. */
  createdAt: number;
  status: UploadStatus;
  attempts: number;
  /** Epoch ms; a waiting photo is not sent before this. */
  nextAttemptAt: number;
  /** 0..1 while sending. */
  progress: number;
  /** The last readable error. */
  error: string | null;
  duplicateOf: { photoId: string; fileName: string } | null;
  keepDuplicate: boolean;
  sentAt: number | null;
}

/** What `enqueue` takes: the photo and where it goes. The queue fills in the rest. */
export type UploadInput = Omit<
  QueuedUpload,
  | "id"
  | "status"
  | "attempts"
  | "nextAttemptAt"
  | "progress"
  | "error"
  | "duplicateOf"
  | "keepDuplicate"
  | "sentAt"
  | "createdAt"
> & { id?: string };

export type SendResult =
  | { kind: "sent" }
  | { kind: "duplicate"; photoId: string; fileName: string }
  /** 400/403/404/413: do not retry. */
  | { kind: "rejected"; error: string }
  /** Network, 5xx, 401, 408, 429: retry later. */
  | { kind: "retry"; error: string };

/** A photo still on the phone after this long needs the crew's attention. */
export const STALE_AFTER_MS = 4 * 60 * 60 * 1000;
/** How long a sent photo's row stays for the "sent today" list. */
export const SENT_KEPT_MS = 24 * 60 * 60 * 1000;

const FIRST_RETRY_MS = 2_000;
const MAX_RETRY_MS = 5 * 60 * 1000;
/** The server's own limit on `device`. */
const MAX_DEVICE_LENGTH = 120;

/** 2s, 4s, 8s ... capped at 5 minutes. `attempt` counts from 1. */
export function nextRetryDelay(attempt: number): number {
  const n = Math.max(1, Math.floor(attempt));
  // 2^9 * 2s already passes the cap, so larger exponents never matter (and never overflow).
  if (n > 9) return MAX_RETRY_MS;
  return Math.min(FIRST_RETRY_MS * 2 ** (n - 1), MAX_RETRY_MS);
}

const UNSENT: ReadonlySet<UploadStatus> = new Set(["waiting", "sending", "failed"]);

/** Not yet on the server and queued more than 4 hours ago. */
export function isStale(item: QueuedUpload, now: number): boolean {
  return UNSENT.has(item.status) && now - item.createdAt > STALE_AFTER_MS;
}

export interface QueueSummary {
  waiting: number;
  sending: number;
  failed: number;
  duplicate: number;
  sent: number;
  /** When the oldest photo still to go up (waiting or sending) was queued. */
  oldestWaitingAt: number | null;
  stale: number;
}

export function summarize(items: QueuedUpload[], now: number = Date.now()): QueueSummary {
  const summary: QueueSummary = {
    waiting: 0,
    sending: 0,
    failed: 0,
    duplicate: 0,
    sent: 0,
    oldestWaitingAt: null,
    stale: 0,
  };
  for (const item of items) {
    summary[item.status] += 1;
    if (isStale(item, now)) summary.stale += 1;
    if (
      (item.status === "waiting" || item.status === "sending") &&
      (summary.oldestWaitingAt === null || item.createdAt < summary.oldestWaitingAt)
    ) {
      summary.oldestWaitingAt = item.createdAt;
    }
  }
  return summary;
}

/** Waiting photos that are due, oldest first, at most `max`. */
export function pickNext(items: QueuedUpload[], now: number, max: number): QueuedUpload[] {
  if (max <= 0) return [];
  return items
    .filter((i) => i.status === "waiting" && i.nextAttemptAt <= now)
    .sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id))
    .slice(0, max);
}

export function createUpload(input: UploadInput, id: string, now: number): QueuedUpload {
  const { id: _given, ...rest } = input;
  return {
    ...rest,
    id,
    createdAt: now,
    status: "waiting",
    attempts: 0,
    nextAttemptAt: now,
    progress: 0,
    error: null,
    duplicateOf: null,
    keepDuplicate: false,
    sentAt: null,
  };
}

/** Why a photo of this size cannot be queued, or null when it can. */
export function sizeError(bytes: number): string | null {
  if (bytes <= 0) return "This photo is empty";
  if (bytes > MAX_UPLOAD_BYTES) return "This photo is over 60 MB";
  return null;
}

/** The item's next state after a send attempt. */
export function applyResult(item: QueuedUpload, result: SendResult, now: number): QueuedUpload {
  switch (result.kind) {
    case "sent":
      return {
        ...item,
        status: "sent",
        // The bytes are on the server now; keep only the row.
        blob: new Blob([]),
        progress: 1,
        error: null,
        duplicateOf: null,
        sentAt: now,
      };
    case "duplicate":
      return {
        ...item,
        status: "duplicate",
        progress: 0,
        error: null,
        duplicateOf: { photoId: result.photoId, fileName: result.fileName },
      };
    case "rejected":
      return {
        ...item,
        status: "failed",
        attempts: item.attempts + 1,
        progress: 0,
        error: result.error,
      };
    case "retry": {
      const attempts = item.attempts + 1;
      return {
        ...item,
        status: "waiting",
        attempts,
        nextAttemptAt: now + nextRetryDelay(attempts),
        progress: 0,
        error: result.error,
      };
    }
  }
}

/** A photo whose send was cut short goes back in line as it was: no extra attempt, no backoff. */
export function backToWaiting(item: QueuedUpload): QueuedUpload {
  return { ...item, status: "waiting", progress: 0 };
}

/**
 * Rows marked "sending" that this worker is not actually sending (a save failed after the
 * send), put back in line. Only meaningful where one tab is the worker.
 */
export function orphanedSending(
  items: QueuedUpload[],
  inFlightIds: ReadonlySet<string>,
): QueuedUpload[] {
  return items.filter((i) => i.status === "sending" && !inFlightIds.has(i.id)).map(backToWaiting);
}

/**
 * What to fix when the worker starts: a photo left "sending" (the app closed mid-send)
 * goes back in line, since the server takes the same id twice; sent rows older than a
 * day are dropped.
 */
export function cleanupOnStart(
  items: QueuedUpload[],
  now: number,
): { put: QueuedUpload[]; remove: string[] } {
  const put: QueuedUpload[] = [];
  const remove: string[] = [];
  for (const item of items) {
    if (item.status === "sending") {
      put.push(backToWaiting(item));
    } else if (item.status === "sent" && now - (item.sentAt ?? item.createdAt) > SENT_KEPT_MS) {
      remove.push(item.id);
    }
  }
  return { put, remove };
}

/** A plain decimal (never exponent notation, which the server refuses). */
function decimal(value: number, places: number): string {
  const text = value.toFixed(places);
  // toFixed keeps "-0.0000000" for tiny negatives that round to zero.
  return Number(text) === 0 ? (0).toFixed(places) : text;
}

/** The text fields of the multipart upload, in the server's wire shape. */
export function uploadFields(item: QueuedUpload): [string, string][] {
  const fields: [string, string][] = [
    ["id", item.id],
    ["albumId", item.albumId],
    ["via", item.via],
  ];
  if (item.itemId) fields.push(["itemId", item.itemId]);
  if (item.capturedAt) fields.push(["capturedAt", item.capturedAt]);
  const { lat, lng } = item;
  if (
    lat !== null &&
    lng !== null &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180
  ) {
    fields.push(["lat", decimal(lat, 7)], ["lng", decimal(lng, 7)]);
  }
  if (item.accuracy !== null && Number.isFinite(item.accuracy) && item.accuracy >= 0) {
    fields.push(["accuracy", decimal(item.accuracy, 2)]);
  }
  const device = item.device?.trim().slice(0, MAX_DEVICE_LENGTH);
  if (device) fields.push(["device", device]);
  if (item.keepDuplicate) fields.push(["keepDuplicate", "1"]);
  return fields;
}

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function serverError(body: unknown): string | null {
  if (typeof body === "object" && body !== null && "error" in body) {
    const { error } = body;
    if (typeof error === "string" && error.trim()) return error;
  }
  return null;
}

const REJECTED_DEFAULTS: Record<number, string> = {
  400: "The server could not accept this photo",
  403: "You can no longer add photos to this project",
  404: "This project or album no longer exists",
};

/** How the upload endpoint's answer (status 0 = no answer at all) maps to a result. */
export function classifyResponse(status: number, text: string): SendResult {
  if (status === 0) return { kind: "retry", error: "No connection to the server" };
  const body = parseJson(text);
  if (status === 200 || status === 201) {
    // Anything else 2xx-shaped is unreadable; resending the same id is safe.
    if (uploadResponseSchema.safeParse(body).success) return { kind: "sent" };
    return { kind: "retry", error: "The server answered with something unexpected" };
  }
  if (status === 409) {
    const dup = uploadDuplicateSchema.safeParse(body);
    if (dup.success) return { kind: "duplicate", ...dup.data.duplicate };
    return { kind: "rejected", error: serverError(body) ?? "The server refused this photo" };
  }
  if (status === 401) return { kind: "retry", error: "Sign in again to keep uploading" };
  if (status === 429) return { kind: "retry", error: "The server is busy, trying again shortly" };
  if (status === 408) return { kind: "retry", error: "The server took too long to answer" };
  if (status === 413) return { kind: "rejected", error: "This photo is too big to upload" };
  if (status >= 500) {
    return { kind: "retry", error: serverError(body) ?? `The server answered ${status}` };
  }
  return {
    kind: "rejected",
    error: serverError(body) ?? REJECTED_DEFAULTS[status] ?? `The server answered ${status}`,
  };
}
